import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { parsePhoneNumber } from "libphonenumber-js"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a phone answer for human-readable display, e.g. "+1 203 219 3851".
 * Uses libphonenumber's international format so a country's trunk prefix never
 * shows doubled (the "+1 12032193851" case when a US lead types the leading 1).
 * Falls back to a plain "<dial> <number>" concat when the value can't be parsed.
 */
export function formatPhoneDisplay(value: { countryCode?: string; number?: string }): string {
  const dialCode = value.countryCode || ""
  const number = value.number || ""
  const input = `${dialCode}${number}`.replace(/\s/g, "")
  if (input) {
    try {
      const parsed = parsePhoneNumber(input.startsWith("+") ? input : `+${input}`)
      if (parsed?.isValid()) return parsed.formatInternational()
    } catch {
      // fall through to best-effort concat
    }
  }
  return dialCode ? `${dialCode} ${number}`.trim() : number.trim()
}

/**
 * ISO country code of a phone answer (e.g. "CA"), read by libphonenumber off the
 * dial code plus the number, so a shared dial code resolves too: +1 416 is
 * Canada, +1 876 Jamaica. Undefined when the number places in no country.
 */
export function phoneCountry(value: { countryCode?: string; number?: string }): string | undefined {
  const input = `${value.countryCode || ""}${value.number || ""}`.replace(/\s/g, "")
  if (!input) return undefined
  try {
    return parsePhoneNumber(input.startsWith("+") ? input : `+${input}`)?.country
  } catch {
    return undefined
  }
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" })

/** English name of an ISO country code ("DE" is "Germany"), or the code itself if it names no region. */
export function countryName(code: string): string {
  try {
    return regionNames.of(code) ?? code
  } catch {
    return code
  }
}
